// @ts-nocheck
import React, { useState } from 'react'
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-blank.svg'
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-01.svg'
import { ReactComponent as CaretDownIcon } from '../../assets/icons/caret-down.svg'
import { ReactComponent as FilterLinesIcon } from '../../assets/icons/filter-lines.svg'
import { ReactComponent as AssetChartIcon } from '../../assets/icons/asset-chart.svg'
import { ReactComponent as AssetListIcon } from '../../assets/icons/asset-list.svg'
import { ReactComponent as CaretGreaterIcon } from '../../assets/icons/caretGreater.svg'
import { ReactComponent as CaretLessIcon } from '../../assets/icons/caretLess.svg'
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-simple.svg'

const AssetPageGraphControls = ({ forecastControls, graphControls, viewControls, onDownloadReport }) => {
  const [forecastMenuOpen, setForecastMenuOpen] = useState(false)
  const [variationMenuOpen, setVariationMenuOpen] = useState(false)
  
  const activeForecast = forecastControls?.activeForecast
  const forecastSchema = forecastControls?.foreCastSchema || {}
  const variations = forecastSchema?.[activeForecast]?.variations || []
  const activeVariationKey = forecastControls?.forecastVariation
  const activeVariationLabel =
    variations.find((variation) => variation.key === activeVariationKey)?.name ||
    activeVariationKey ||
    'Select Forecast'
  const activeForecastLabel =
    forecastSchema?.[activeForecast]?.name || activeForecast || 'Select Forecast'
  const hasVariations = variations.length > 0
  
  const handleSelectVariation = (key) => {
    forecastControls?.onVariationChange?.(key)
    setVariationMenuOpen(false)
  }
  
  const ActiveForecastIcon = forecastSchema?.[activeForecast]?.Icon
  const displayView = viewControls?.displayView
  
  const range = graphControls?.getCurrentRange?.()
  const formatDate = (value) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(date)
  }
  
  const startLabel = formatDate(range?.start)
  const endLabel = formatDate(range?.end)
  const dateLabel = startLabel && endLabel ? `${startLabel} - ${endLabel}` : 'No date available'
  
  return (
    <div className="flex items-center justify-between gap-3 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        {/* View Toggle */}
        <div className="flex items-center gap-1 border-r border-eai-bg-lt2 pr-3">
          <button
            type="button"
            className={`inline-flex h-[30px] w-[30px] items-center justify-center rounded-[8px] border ${
              displayView === 'table' ? 'border-[#5B6F72] bg-[#eef1f2]' : 'border-[#c7d6d9] bg-[#f6f8f9]'
            }`}
            onClick={() => viewControls?.setDisplayView?.('table')}
            aria-pressed={displayView === 'table'}
            aria-label="Table view"
          >
            <AssetListIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            className={`inline-flex h-[30px] w-[30px] items-center justify-center rounded-[8px] border ${
              displayView === 'graph' ? 'border-[#5B6F72] bg-[#eef1f2]' : 'border-[#e8eced] bg-white'
            }`}
            onClick={() => viewControls?.setDisplayView?.('graph')}
            aria-pressed={displayView === 'graph'}
            aria-label="Graph view"
          >
            <AssetChartIcon className="h-6 w-6" />
          </button>
        </div>
        
        {/* Model Selector */}
        <div className="relative">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-[#1F2A37]"
            onClick={() => forecastControls?.foreCastSchema && setForecastMenuOpen((prev) => !prev)}
            aria-expanded={forecastMenuOpen}
            aria-haspopup="listbox"
          >
            {ActiveForecastIcon ? (
              <ActiveForecastIcon className="h-4 w-4 stroke-[#1F2A37]" />
            ) : null}
            {activeForecastLabel}
            <CaretDownIcon className="h-1 w-2" />
          </button>
          {forecastMenuOpen && forecastControls?.foreCastSchema && (
            <div className="absolute left-0 top-full z-10 mt-2 w-56 rounded-[8px] border border-eai-bg-lt2 bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.08)]">
              {Object.entries(forecastSchema).map(([key, forecast]) => {
                const Icon = forecast?.Icon
                const iconClass =
                  key === activeForecast ? 'h-4 w-4 stroke-[#1F2A37]' : 'h-4 w-4 stroke-[#84979A]'
                return (
                  <button
                    key={key}
                    type="button"
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                      key === activeForecast
                        ? 'bg-[#f6f8f9] text-[#1F2A37]'
                        : 'text-[#4B5563] hover:bg-[#f6f8f9]'
                    }`}
                    onClick={() => {
                      forecastControls.onForecastSelect(key)
                      setForecastMenuOpen(false)
                    }}
                    role="option"
                    aria-selected={key === activeForecast}
                  >
                    {Icon ? <Icon className={iconClass} /> : null}
                    <div>{forecast?.name || key}</div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
        
        {/* Graph Variant */}
        <div className="relative">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-[#1F2A37]"
            onClick={() => hasVariations && setVariationMenuOpen((prev) => !prev)}
            aria-expanded={variationMenuOpen}
            aria-haspopup="listbox"
          >
            <FilterLinesIcon className="h-3 w-3" />
            {activeVariationLabel}
          </button>
          {variationMenuOpen && hasVariations && (
            <div className="absolute left-0 top-full z-10 mt-2 w-48 rounded-[8px] border border-eai-bg-lt2 bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.08)]">
              {variations.map((variation) => (
                <button
                  key={variation.key}
                  type="button"
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs ${
                    variation.key === activeVariationKey
                      ? 'bg-[#f6f8f9] text-[#1F2A37]'
                      : 'text-[#4B5563] hover:bg-[#f6f8f9]'
                  }`}
                  onClick={() => handleSelectVariation(variation.key)}
                  role="option"
                  aria-selected={variation.key === activeVariationKey}
                >
                  <div>{variation.name || variation.key}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* DateTime Controls */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-[8px] border border-[#e8eced] bg-white"
          >
            <CaretLessIcon className="h-2 w-1" />
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-[8px] border border-[#e8eced] bg-white px-3 py-1.5 text-xs font-medium text-[#101010]"
            onClick={() => graphControls?.openTimeRange && graphControls.openTimeRange()}
          >
            {dateLabel}
            <CalendarIcon className="h-3 w-3" />
          </button>
          <button
            type="button"
            className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-[8px] border border-[#e8eced] bg-white"
          >
            <CaretGreaterIcon className="h-2 w-1" />
          </button>
        </div>
        
        {/* Datasets */}
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-[#1F2A37]"
          onClick={() => graphControls?.openDataset && graphControls.openDataset()}
        >
          <CalendarIcon className="h-3 w-3" />
          Datasets
        </button>
      </div>
      
      {/* Download */}
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-[6px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-black"
        onClick={onDownloadReport}
      >
        <DownloadIcon className="h-3 w-3" />
        Download
      </button>
    </div>
  )
}

export default AssetPageGraphControls